import { formatCurrencyCompact, formatSignedPct } from '../cfoFormat'
import { formatRankShift } from '../ceo/ceoFormat'
import './BulletBars.css'

export type BulletBarItem = {
  key: string
  label: string
  actual: number | null
  /** The basis the actual is measured against (plan / forecast / prior year …) — drawn as a tick. */
  comparative?: number | null
  /** A translucent reference bar behind the actual (prior year when the basis is something else). */
  ghost?: number | null
  share?: number | null
  rank?: number | null
  priorRank?: number | null
  deltaRank?: number | null
  secondary?: number | null
  secondaryLabel?: string | null
}

export type BulletBarsProps = {
  items: BulletBarItem[]
  ariaLabel: string
  comparativeLabel?: string
  ghostLabel?: string
  cumulativeShare?: boolean
  formatValue?: (value: number) => string
  /** When provided, rows render as buttons; the row whose key equals `selectedKey` is marked pressed. */
  onSelect?: (item: BulletBarItem) => void
  selectedKey?: string | null
  className?: string
}

export type BulletScale = { min: number; max: number }

export function computeBulletScale(items: BulletBarItem[]): BulletScale {
  const values = items
    .flatMap((item) => [item.actual, item.comparative, item.ghost])
    .filter((value): value is number => value != null && Number.isFinite(value))
  if (!values.length) return { min: 0, max: 1 }
  const max = Math.max(0, ...values)
  const min = Math.min(0, ...values)
  return { min, max: max === min ? max + 1 : max }
}

export function rankShiftLabel(deltaRank?: number | null): string {
  return formatRankShift(deltaRank)
}

export function cumulativeShares(items: BulletBarItem[]): number[] {
  let running = 0
  return items.map((item) => {
    running += item.share ?? 0
    return running
  })
}

/** Signed variance of actual against its comparative, as a fraction; null when not computable. */
export function bulletDeltaPct(item: Pick<BulletBarItem, 'actual' | 'comparative'>): number | null {
  const actual = item.actual
  const comp = item.comparative
  if (actual == null || comp == null || !Number.isFinite(actual) || !Number.isFinite(comp)) return null
  if (Math.abs(comp) < 1e-9) return null
  return (actual - comp) / Math.abs(comp)
}

export function BulletBars({
  items,
  ariaLabel,
  comparativeLabel = 'plan',
  ghostLabel = 'prior year',
  cumulativeShare = false,
  formatValue,
  onSelect,
  selectedKey,
  className,
}: BulletBarsProps) {
  const scale = computeBulletScale(items)
  const span = scale.max - scale.min || 1
  const fmt = formatValue || ((value: number) => formatCurrencyCompact(value))
  const shares = cumulativeShare ? cumulativeShares(items) : []
  const pct = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return 0
    return Math.max(0, Math.min(100, ((value - scale.min) / span) * 100))
  }
  const hasComparative = items.some((item) => item.comparative != null && Number.isFinite(item.comparative))
  const hasGhost = items.some((item) => item.ghost != null && Number.isFinite(item.ghost))
  const hasSecondary = items.some((item) => item.secondary != null && Number.isFinite(item.secondary))
  const hasShare = items.some((item) => item.share != null && Number.isFinite(item.share))
  const hasRank = items.some((item) => item.rank != null || item.deltaRank != null)
  const interactive = typeof onSelect === 'function'

  return (
    <div
      className={`ceo-bullet-bars ${className || ''}`.trim()}
      role="group"
      aria-label={ariaLabel}
      data-interactive={interactive ? 'true' : undefined}
    >
      {items.map((item, index) => {
        const actualPct = pct(item.actual)
        const compPct = pct(item.comparative)
        const ghostPct = pct(item.ghost)
        const prepared = item.actual != null && Number.isFinite(item.actual)
        const delta = hasComparative ? bulletDeltaPct(item) : null
        const deltaTone = delta == null ? 'flat' : delta > 0.0005 ? 'pos' : delta < -0.0005 ? 'neg' : 'flat'
        const selected = interactive && selectedKey != null && selectedKey === item.key
        const rankNumber = item.rank ?? index + 1
        const rowClass = `ceo-bullet-row${selected ? ' is-selected' : ''}${prepared ? '' : ' is-empty'}`
        const shareText = cumulativeShare && shares[index] != null
          ? `${Math.round(shares[index] * 100)}% cum`
          : item.share != null && Number.isFinite(item.share)
            ? `${Math.round(item.share * 100)}%`
            : null
        const secondaryText = item.secondary != null && Number.isFinite(item.secondary)
          ? `${item.secondaryLabel || 'GM'} ${Math.round(item.secondary * 100)}%`
          : null

        const cells = (
          <>
            <span className="ceo-bullet-label" title={item.label}>
              <b>{item.label}</b>
            </span>
            <span className="ceo-bullet-track" aria-hidden="true">
              {item.ghost != null && Number.isFinite(item.ghost) ? (
                <i className="ceo-bullet-ghost" style={{ width: `${ghostPct}%` }} />
              ) : null}
              <i className="ceo-bullet-fill" style={{ width: `${actualPct}%` }} />
              {item.comparative != null && Number.isFinite(item.comparative) ? (
                <i className="ceo-bullet-tick" style={{ left: `${compPct}%` }} />
              ) : null}
            </span>
            <span className="ceo-bullet-value">
              {prepared ? fmt(item.actual as number) : <em>Not prepared</em>}
            </span>
            {hasComparative ? (
              <span className={`ceo-bullet-delta is-${deltaTone}`}>
                {delta == null ? <em>—</em> : formatSignedPct(delta)}
              </span>
            ) : null}
            {hasShare || hasSecondary ? (
              <span className="ceo-bullet-share">
                {shareText ? <span>{shareText}</span> : <em>—</em>}
                {secondaryText ? <small>{secondaryText}</small> : null}
              </span>
            ) : null}
            {hasRank ? (
              <span className="ceo-bullet-rank">
                <span>#{rankNumber}</span>
                {item.deltaRank != null && Number.isFinite(item.deltaRank) && item.deltaRank !== 0 ? (
                  <small className={item.deltaRank < 0 ? 'is-up' : 'is-down'} title={`Rank ${rankShiftLabel(item.deltaRank)} vs prior`}>
                    {rankShiftLabel(item.deltaRank)}
                  </small>
                ) : null}
              </span>
            ) : null}
          </>
        )

        if (interactive) {
          return (
            <button
              type="button"
              className={rowClass}
              key={item.key}
              onClick={() => onSelect?.(item)}
              aria-pressed={selected}
              aria-label={`${item.label}: ${prepared ? fmt(item.actual as number) : 'not prepared'}`}
            >
              {cells}
            </button>
          )
        }
        return (
          <div className={rowClass} key={item.key}>
            {cells}
          </div>
        )
      })}
      {hasComparative || hasGhost ? (
        <div className="ceo-bullet-legend">
          <span>
            <i className="ceo-bullet-legend-actual" /> actual
          </span>
          {hasComparative ? (
            <span>
              <i className="ceo-bullet-legend-plan" /> {comparativeLabel}
            </span>
          ) : null}
          {hasGhost ? (
            <span>
              <i className="ceo-bullet-legend-ghost" /> {ghostLabel}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default BulletBars
